import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Card, CardContent, Typography, Button, CircularProgress, Alert, Box, TextField, Divider, Collapse } from '@mui/material';
import { Email as EmailIcon, Error as ErrorIcon, Check as CheckIcon, Google, Login as LoginIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useAppDispatch } from '@/hooks/redux';
import { loginSuccess } from '@/store/authSlice';
import { authManager } from '@/services/authManager';
import { oauthService, OAuthConfig } from '@/services/oauthService';

interface InvitationInfo {
  email: string;
  organizationName: string;
  role: string;
  userExists: boolean;
}

const AcceptInvitationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });
  const [loginPassword, setLoginPassword] = useState('');
  const [isAccepting, setIsAccepting] = useState(false);
  const [oauthConfig, setOauthConfig] = useState<OAuthConfig | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const oauthLoaded = oauthConfig !== null;
  const hasSocialProviders = !!oauthConfig?.google.enabled;
  
  const token = searchParams.get('token');
  const isLoggedIn = authManager.isAuthenticated();

  const validateInvitation = async () => {
    if (!token) return;

    try {
      const response = await fetch(`/api/v1/organizations/invitations/${token}/validate`, {
        method: 'GET',
      });

      const data = await response.json();

      if (response.ok) {
        setInvitationInfo(data.data);
      } else {
        setError(data.message || 'Invalid invitation');
      }
    } catch (err: any) {
      setError('Failed to validate invitation');
    }
  };

  const handleAcceptInvitation = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);
    setIsAccepting(true);

    try {
      if (!isLoggedIn && invitationInfo?.userExists) {
        // User exists - sign in first, then accept invitation
        const loginResponse = await fetch('/api/v1/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: invitationInfo.email,
            password: loginPassword
          })
        });

        const loginData = await loginResponse.json();

        if (!loginResponse.ok) {
          throw new Error(loginData.error?.message || loginData.message || 'Invalid password');
        }

        // Now accept the invitation with the new token
        const acceptResponse = await fetch(`/api/v1/organizations/invitations/${token}/accept`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${loginData.token}`,
            'Content-Type': 'application/json',
          },
        });

        const acceptData = await acceptResponse.json();

        if (!acceptResponse.ok) {
          throw new Error(acceptData.message || 'Failed to accept invitation');
        }

        // Store token and navigate immediately BEFORE updating Redux
        // This prevents the component from remounting and re-validating the consumed invitation
        localStorage.setItem('token', acceptData.token || loginData.token);
        navigate('/dashboard');

        // Update Redux store after navigation is initiated
        dispatch(loginSuccess({
          token: acceptData.token || loginData.token,
          user: acceptData.user || loginData.user
        }));
      } else if (!isLoggedIn) {
        // Create account and accept invitation
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }

        // Validate password strength
        if (formData.password.length < 8) {
          throw new Error('Password must be at least 8 characters');
        }
        if (!/[A-Z]/.test(formData.password)) {
          throw new Error('Password must contain at least one uppercase letter');
        }
        if (!/[a-z]/.test(formData.password)) {
          throw new Error('Password must contain at least one lowercase letter');
        }
        if (!/[0-9]/.test(formData.password)) {
          throw new Error('Password must contain at least one number');
        }

        const response = await fetch(`/api/v1/organizations/invitations/${token}/accept-with-signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name,
            password: formData.password
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to create account and accept invitation');
        }

        // Store token and redirect immediately
        localStorage.setItem('token', data.token);

        // Update Redux store with new user data
        if (data.user) {
          dispatch(loginSuccess({
            token: data.token,
            user: data.user
          }));
        }

        // Redirect immediately for new users to avoid token validation issues
        navigate('/dashboard');
      } else {
        // Just accept invitation
        const authToken = await authManager.getAccessToken();
        const response = await fetch(`/api/v1/organizations/invitations/${token}/accept`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to accept invitation');
        }

        // Store token and navigate immediately BEFORE updating Redux
        // This prevents the component from remounting and re-validating the consumed invitation
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        navigate('/dashboard');

        // Update Redux store after navigation is initiated
        if (data.user) {
          dispatch(loginSuccess({
            token: data.token,
            user: data.user
          }));
        }
      }

    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      return;
    }

    // Don't validate invitation if we're in the process of accepting
    if (isAccepting) {
      return;
    }

    validateInvitation();
  }, [token, isAccepting]);

  useEffect(() => {
    const loadOAuthConfig = async () => {
      try {
        const config = await oauthService.getConfig();
        setOauthConfig(config);
      } catch {
        setOauthConfig(null);
      }
    };
    loadOAuthConfig();
  }, []);

  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Invalid Invitation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              No invitation token was provided.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card>
        <CardContent sx={{ py: 4 }}>
            <>
              <Typography variant="h4" gutterBottom textAlign="center">
                Join Organization
              </Typography>
              
              {invitationInfo && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  You've been invited to join <strong>{invitationInfo.organizationName}</strong> as a {invitationInfo.role.toLowerCase()}.
                </Alert>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {!isLoggedIn && invitationInfo && invitationInfo.userExists && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Sign In to Accept
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    An account already exists for <strong>{invitationInfo.email}</strong>. Please sign in to accept the invitation.
                  </Typography>

                  <TextField
                    fullWidth
                    label="Email"
                    value={invitationInfo.email}
                    disabled
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAcceptInvitation();
                      }
                    }}
                    sx={{ mb: 3 }}
                    required
                  />

                  <Divider sx={{ mb: 3 }} />
                </>
              )}

              {!isLoggedIn && invitationInfo && !invitationInfo.userExists && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Create Your Account
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    You'll be joining as <strong>{invitationInfo.email}</strong>. Choose how to create your account.
                  </Typography>

                  {hasSocialProviders && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                      {oauthConfig?.google.enabled && (
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<Google />}
                          onClick={() => oauthService.loginWithGoogle({ invitationToken: token! })}
                          sx={{
                            borderColor: '#4285F4',
                            color: '#4285F4',
                            '&:hover': {
                              borderColor: '#357ABD',
                              backgroundColor: 'rgba(66, 133, 244, 0.04)',
                            },
                          }}
                        >
                          Continue with Google
                        </Button>
                      )}
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<EmailIcon />}
                        onClick={() => setShowEmailForm(!showEmailForm)}
                        sx={{
                          borderColor: showEmailForm ? 'primary.main' : 'grey.400',
                          color: showEmailForm ? 'primary.main' : 'text.primary',
                          '&:hover': {
                            borderColor: showEmailForm ? 'primary.dark' : 'grey.600',
                            backgroundColor: showEmailForm ? 'rgba(25, 118, 210, 0.04)' : 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        Sign up with Email
                      </Button>
                    </Box>
                  )}

                  <Collapse in={showEmailForm || (oauthLoaded && !hasSocialProviders)}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      sx={{ mb: 2 }}
                      required
                    />

                    <TextField
                      fullWidth
                      label="Password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      helperText="At least 8 characters with uppercase, lowercase, and a number"
                      sx={{ mb: 2 }}
                      required
                    />

                    <TextField
                      fullWidth
                      label="Confirm Password"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAcceptInvitation();
                        }
                      }}
                      sx={{ mb: 3 }}
                      required
                    />
                  </Collapse>

                  <Divider sx={{ mb: 3 }} />
                </>
              )}

              {isLoggedIn && invitationInfo && (
                <>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                   Click below to accept the invitation and join the organization.
                  </Typography>
                </>
              )}

              {(isLoggedIn || invitationInfo?.userExists || showEmailForm || (!invitationInfo?.userExists && oauthLoaded && !hasSocialProviders)) && (
                <Box textAlign="center">
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleAcceptInvitation}
                    disabled={
                      loading ||
                      !invitationInfo ||
                      (!isLoggedIn && invitationInfo?.userExists && !loginPassword) ||
                      (!isLoggedIn && !invitationInfo?.userExists && (!formData.name || !formData.password || !formData.confirmPassword))
                    }
                    startIcon={loading ? <CircularProgress size={20} /> : isLoggedIn ? <CheckIcon /> : invitationInfo?.userExists ? <LoginIcon /> : <PersonAddIcon />}
                  >
                    {loading
                      ? 'Processing...'
                      : isLoggedIn
                        ? 'Accept Invitation'
                        : invitationInfo?.userExists
                          ? 'Sign In & Accept'
                          : 'Create Account & Join'}
                  </Button>
                </Box>
              )}
            </>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AcceptInvitationPage;
